import React, { Fragment } from "react";
import PropTypes from "prop-types";
import {
	TableRow,
	TableCell,
	LinearProgress,
	IconButton,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import ColourConstants from "helpers/colourConstants";
import PopupMenu from "components/Elements/PopupMenu";
import { ReactComponent as MenuIcon } from "assets/icons/3dot-icon.svg";

import TableStyle from "styles/application/TableStyle";
import useGroupedTableStyles, {
	INDENT_PER_LEVEL,
	BASE_INDENT,
} from "./groupedTableStyles";

const AT = TableStyle();

const GroupedTableRow = ({
	row,
	depth,
	menuKeyPrefix,
	tableConfig,
	expandColumnIndex,
	childData,
	childLoading,
	expandedRows,
	onExpandToggle,
	isReadOnly,
	menuState,
	getCellValue,
	getMenuData,
	isTreeView = true,
	testIdPrefix,
}) => {
	const { classes } = useGroupedTableStyles();
	const { selectedData, anchorEl, handlePopup, handleClosePopup } = menuState;

	const isExpanded = (rowId) =>
		expandedRows instanceof Set
			? expandedRows.has(rowId)
			: !!expandedRows[rowId];

	const expanded = isTreeView && isExpanded(row.id);
	const indent = isTreeView ? BASE_INDENT + depth * INDENT_PER_LEVEL : 0;
	const isRoot = depth === 0;
	const treeCol = expandColumnIndex ?? 0;
	// Hide chevron if children were fetched but came back empty (e.g. filtered out in Site Asset mode)
	const loadedEmpty = childData[row.id] && childData[row.id].length === 0;
	const hasVisibleChildren = row.hasChildren && !loadedEmpty;

	const renderActionCell = (targetRow, menuKey) => (
		<AT.DataCell className={classes.actionColumn}>
			<AT.CellContainer>
				<AT.DotMenu onClick={(e) => handlePopup(e, menuKey)}>
					<AT.TableMenuButton data-testid={testIdPrefix ? `${testIdPrefix}-row-action-${targetRow.id}` : undefined}>
						<MenuIcon />
					</AT.TableMenuButton>
					<PopupMenu
						index={menuKey}
						selectedData={selectedData}
						anchorEl={anchorEl}
						id={targetRow.id}
						clickAwayHandler={handleClosePopup}
						menuData={getMenuData(targetRow)}
						testIdPrefix={testIdPrefix ? `${testIdPrefix}-row-${targetRow.id}` : undefined}
					/>
				</AT.DotMenu>
			</AT.CellContainer>
		</AT.DataCell>
	);

	const renderChildren = () => {
		if (!isTreeView || !expanded) return null;

		if (childLoading[row.id]) {
			return (
				<TableRow className={classes.loadingRow}>
					<TableCell
						colSpan={
							tableConfig.columns.length + (isReadOnly ? 0 : 1)
						}
						style={{ padding: 0 }}
					>
						<LinearProgress />
					</TableCell>
				</TableRow>
			);
		}

		const children = childData[row.id] || [];
		return children.map((childRow) => (
			<GroupedTableRow
				key={childRow.id}
				row={childRow}
				depth={depth + 1}
				menuKeyPrefix={`child-d${depth + 1}`}
				tableConfig={tableConfig}
				expandColumnIndex={expandColumnIndex}
				childData={childData}
				childLoading={childLoading}
				expandedRows={expandedRows}
				onExpandToggle={onExpandToggle}
				isReadOnly={isReadOnly}
				menuState={menuState}
				getCellValue={getCellValue}
				getMenuData={getMenuData}
				isTreeView={isTreeView}
				testIdPrefix={testIdPrefix}
			/>
		));
	};

	const renderExpandCell = (column) => (
		<div className={classes.expandCell}>
			{isTreeView && hasVisibleChildren ? (
				<IconButton
					className={classes.expandIcon}
					onClick={() => onExpandToggle(row.id)}
					size="small"
					style={
						isRoot && expanded ? { color: "#FFFFFF" } : undefined
					}
				>
					{expanded ? (
						<KeyboardArrowDownIcon />
					) : (
						<KeyboardArrowRightIcon />
					)}
				</IconButton>
			) : (
				isTreeView && <span className={classes.expandIconPlaceholder} />
			)}
			<AT.TableBodyText
				style={
					isRoot && expanded ? { color: "#FFFFFF" } : undefined
				}
			>
				{getCellValue(row, column)}
			</AT.TableBodyText>
		</div>
	);

	const renderPlainCell = (column) => (
		<AT.TableBodyText
			style={isRoot && expanded ? { color: "#FFFFFF" } : undefined}
		>
			{getCellValue(row, column)}
		</AT.TableBodyText>
	);

	return (
		<Fragment>
			<TableRow
				data-testid={testIdPrefix ? `${testIdPrefix}-row-${row.id}` : undefined}
				hover
				className={!isRoot && isTreeView ? classes.childRow : undefined}
				style={
					isRoot && isTreeView
						? {
								background: expanded
									? ColourConstants.tableRowExpand
									: ColourConstants.tableRowNormal,
							}
						: undefined
				}
			>
				{tableConfig.columns.map((column, colIndex) => (
					<AT.DataCell
						key={`${row.id}-${column}`}
						className={classes.nameRow}
						style={
							colIndex === treeCol && isTreeView
								? { paddingLeft: indent }
								: undefined
						}
					>
						{colIndex === treeCol && isTreeView
							? renderExpandCell(column)
							: renderPlainCell(column)}
					</AT.DataCell>
				))}
				{!isReadOnly &&
					renderActionCell(row, `${menuKeyPrefix}-${row.id}`)}
			</TableRow>
			{renderChildren()}
		</Fragment>
	);
};

GroupedTableRow.propTypes = {
	row: PropTypes.object.isRequired,
	depth: PropTypes.number.isRequired,
	menuKeyPrefix: PropTypes.string.isRequired,
	tableConfig: PropTypes.object.isRequired,
	expandColumnIndex: PropTypes.number,
	childData: PropTypes.object.isRequired,
	childLoading: PropTypes.object.isRequired,
	expandedRows: PropTypes.oneOfType([
		PropTypes.instanceOf(Set),
		PropTypes.object,
	]).isRequired,
	onExpandToggle: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool.isRequired,
	menuState: PropTypes.object.isRequired,
	getCellValue: PropTypes.func.isRequired,
	getMenuData: PropTypes.func.isRequired,
	isTreeView: PropTypes.bool,
	testIdPrefix: PropTypes.string,
};

export default GroupedTableRow;
